/**
 * Helpers for dealing with the browser's "Local Network Access" (LNA) permission
 * — the successor to Private Network Access. When a secure (HTTPS) page tries to
 * reach a loopback/local address such as the Printer Adapter Service on
 * http://localhost:6700, Chrome may block the request and/or show a permission
 * prompt ("This site wants to connect to devices on your local network").
 *
 * IMPORTANT: the prompt cannot be opened imperatively. It is shown by the browser
 * when an actual request to a local address is made, and only reliably during a
 * user gesture (e.g. a click). `probeLocalNetwork` performs that request so the
 * prompt appears, and classifies the outcome so the UI can show a precise message.
 */

export type LocalNetworkProbeResult =
  | 'reachable' // request went through — adapter is up and access is allowed
  | 'permission-denied' // browser blocked access to the local network
  | 'unreachable' // access allowed (or undecided) but nothing answered — adapter is likely not running
  | 'insecure-context'; // page isn't a secure context, so LNA can't be granted at all

/**
 * Best-effort read of the LNA permission state via the Permissions API.
 * Returns `null` when the browser doesn't expose this permission yet.
 */
export async function queryLocalNetworkPermission(): Promise<PermissionState | null> {
  try {
    // `local-network-access` is not in the standard PermissionName union yet.
    const status = await navigator.permissions.query({
      name: 'local-network-access' as PermissionName
    });
    return status.state;
  } catch {
    return null;
  }
}

/**
 * Fires a lightweight request at the adapter to (a) trigger the LNA prompt if it
 * hasn't been answered, and (b) figure out *why* a connection would fail.
 *
 * Call this from a user gesture (the toggle click) so the prompt is allowed to show.
 */
export async function probeLocalNetwork(baseUrl: string, timeoutMs = 4000): Promise<LocalNetworkProbeResult> {
  // LNA is only grantable from a secure context. Plain http:// (non-localhost)
  // pages are blocked outright with no prompt — surface that distinctly.
  if (!window.isSecureContext) {
    return 'insecure-context';
  }

  const before = await queryLocalNetworkPermission();
  if (before === 'denied') {
    return 'permission-denied';
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // `no-cors` lets us reach the adapter without a CORS handshake; we only care
    // whether the request is allowed to leave the browser, not about the body.
    // `targetAddressSpace` (Private Network Access) tells Chrome the target is local.
    await fetch(baseUrl, {
      mode: 'no-cors',
      signal: controller.signal,
      // @ts-expect-error non-standard PNA option, ignored by unsupported browsers
      targetAddressSpace: 'local'
    });
    return 'reachable';
  } catch {
    // Both "blocked by permission" and "connection refused" throw TypeError:
    // Failed to fetch, so re-check the permission to disambiguate.
    const after = await queryLocalNetworkPermission();
    if (after === 'denied') {
      return 'permission-denied';
    }
    // Permission granted/undecided but the request still failed → nothing is
    // listening on the local port.
    return 'unreachable';
  } finally {
    clearTimeout(timer);
  }
}
